import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  createCategory,
  getCategoriesByUser,
  updateCategory,
} from '../api/categories.ts'
import { createTicker, getTickersByUser, updateTicker } from '../api/tickers.ts'
import type { Category, Ticker } from '../types.ts'

type ParsedCategoryRow = {
  name: string
  slug: string
  path: string
  parentPath: string
}

type ParsedTickerRow = {
  ticker: string
  categoryPath: string
}

type UseCategoriesTickersImportResult = {
  executeImport: (text: string) => Promise<string>
  isImporting: boolean
}

export function useCategoriesTickersImport(userId: number): UseCategoriesTickersImportResult {
  const queryClient = useQueryClient()
  const [isImporting, setIsImporting] = useState(false)

  async function executeImport(text: string): Promise<string> {
    if (isImporting) {
      return '[aviso] Ja existe uma importacao em andamento.'
    }

    const normalizedText = text.trim()
    if (!normalizedText) {
      return '[erro] Cole o arquivo de exportacao antes de importar.'
    }

    setIsImporting(true)

    try {
      const { categories, tickers } = parseCategoriesTickersExport(normalizedText)
      const currentCategories = await getCategoriesByUser(userId)
      const currentTickers = await getTickersByUser(userId)

      const syncedCategories = await upsertCategories({
        userId,
        importedCategories: categories,
        currentCategories,
      })

      const syncedTickers = await upsertTickers({
        userId,
        importedTickers: tickers,
        currentTickers,
        categoriesByPath: syncedCategories,
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['categories', userId] }),
        queryClient.invalidateQueries({ queryKey: ['tickers', userId] }),
      ])

      return [
        `[ok] Importacao concluida.`,
        `[ok] Categorias processadas: ${categories.length}`,
        `[ok] Tickers processados: ${tickers.length}`,
        `[ok] Categorias sincronizadas: ${syncedCategories.size}`,
        `[ok] Tickers sincronizados: ${syncedTickers}`,
      ].join('\n')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro desconhecido'
      return `[erro] Falha na importacao: ${message}`
    } finally {
      setIsImporting(false)
    }
  }

  return {
    executeImport,
    isImporting,
  }
}

function parseCategoriesTickersExport(text: string) {
  const lines = text.split(/\r?\n/)

  if (lines[0]?.trim() !== '### my-tickers') {
    throw new Error('Cabecalho invalido: primeira linha deve ser "### my-tickers".')
  }

  if (lines[1]?.trim() !== '### export-categories-tickers') {
    throw new Error(
      'Cabecalho invalido: segunda linha deve ser "### export-categories-tickers".',
    )
  }

  const categoriesMarkerIndex = lines.findIndex((line) => line.trim() === '### categories')
  const tickersMarkerIndex = lines.findIndex((line) => line.trim() === '### tickers')

  if (categoriesMarkerIndex === -1 || tickersMarkerIndex === -1) {
    throw new Error('Blocos obrigatorios "categories" e "tickers" nao encontrados.')
  }

  const categoryLines = lines
    .slice(categoriesMarkerIndex + 1, tickersMarkerIndex)
    .filter((line) => line.trim().length > 0)

  const tickerLines = lines.slice(tickersMarkerIndex + 1).filter((line) => line.trim().length > 0)

  if (categoryLines[0] !== 'name\tslug\tpath\tparent_path') {
    throw new Error('Header invalido no bloco categories.')
  }

  if (tickerLines[0] !== 'ticker\tcategory_path') {
    throw new Error('Header invalido no bloco tickers.')
  }

  const categories = categoryLines.slice(1).map(parseCategoryRow)
  const tickers = tickerLines.slice(1).map(parseTickerRow)

  return { categories, tickers }
}

function parseCategoryRow(line: string): ParsedCategoryRow {
  const [name = '', slug = '', path = '', parentPath = ''] = line.split('\t')

  if (!name.trim() || !slug.trim() || !path.trim()) {
    throw new Error(`Linha de categoria invalida: "${line}"`)
  }

  return {
    name: name.trim(),
    slug: slug.trim(),
    path: path.trim(),
    parentPath: parentPath.trim(),
  }
}

function parseTickerRow(line: string): ParsedTickerRow {
  const [ticker = '', categoryPath = ''] = line.split('\t')

  if (!ticker.trim()) {
    throw new Error(`Linha de ticker invalida: "${line}"`)
  }

  return {
    ticker: ticker.trim().toUpperCase(),
    categoryPath: categoryPath.trim(),
  }
}

async function upsertCategories({
  userId,
  importedCategories,
  currentCategories,
}: {
  userId: number
  importedCategories: ParsedCategoryRow[]
  currentCategories: Category[]
}) {
  const categoriesByPath = new Map<string, Category>()
  const categoriesById = new Map(currentCategories.map((category) => [category.id, category]))

  for (const category of currentCategories) {
    categoriesByPath.set(buildCategoryPath(category, categoriesById), category)
  }

  const sortedImportedCategories = [...importedCategories].sort(
    (a, b) => getPathDepth(a.path) - getPathDepth(b.path) || a.path.localeCompare(b.path),
  )

  for (const importedCategory of sortedImportedCategories) {
    const parentCategory = importedCategory.parentPath
      ? categoriesByPath.get(importedCategory.parentPath) ?? null
      : null

    if (importedCategory.parentPath && !parentCategory) {
      throw new Error(`Categoria pai nao encontrada: "${importedCategory.parentPath}"`)
    }

    const existingCategory = categoriesByPath.get(importedCategory.path) ?? null

    if (!existingCategory) {
      const createdCategory = await createCategory({
        user_id: userId,
        name: importedCategory.name,
        slug: importedCategory.slug,
        parent_id: parentCategory?.id ?? null,
      })

      categoriesByPath.set(importedCategory.path, createdCategory)
      categoriesById.set(createdCategory.id, createdCategory)
      continue
    }

    const nextParentId = parentCategory?.id ?? null
    const shouldUpdate =
      existingCategory.name !== importedCategory.name ||
      existingCategory.slug !== importedCategory.slug ||
      (existingCategory.parent_id ?? null) !== nextParentId

    if (!shouldUpdate) continue

    const updatedCategory = await updateCategory(existingCategory.id, {
      user_id: userId,
      name: importedCategory.name,
      slug: importedCategory.slug,
      parent_id: nextParentId,
    })

    categoriesByPath.set(importedCategory.path, updatedCategory)
    categoriesById.set(updatedCategory.id, updatedCategory)
  }

  return categoriesByPath
}

async function upsertTickers({
  userId,
  importedTickers,
  currentTickers,
  categoriesByPath,
}: {
  userId: number
  importedTickers: ParsedTickerRow[]
  currentTickers: Ticker[]
  categoriesByPath: Map<string, Category>
}) {
  const tickersBySymbol = new Map(currentTickers.map((ticker) => [ticker.ticker.toUpperCase(), ticker]))
  let processedCount = 0

  for (const importedTicker of importedTickers) {
    const categoryId = importedTicker.categoryPath
      ? categoriesByPath.get(importedTicker.categoryPath)?.id ?? null
      : null

    if (importedTicker.categoryPath && categoryId == null) {
      throw new Error(`Categoria do ticker nao encontrada: "${importedTicker.categoryPath}"`)
    }

    const existingTicker = tickersBySymbol.get(importedTicker.ticker) ?? null

    if (!existingTicker) {
      const createdTicker = await createTicker({
        user_id: userId,
        ticker: importedTicker.ticker,
        category_id: categoryId,
        cnpj: null,
      })
      tickersBySymbol.set(importedTicker.ticker, createdTicker)
      processedCount += 1
      continue
    }

    if ((existingTicker.category_id ?? null) === categoryId) {
      processedCount += 1
      continue
    }

    const updatedTicker = await updateTicker(existingTicker.id, {
      category_id: categoryId,
    })
    tickersBySymbol.set(importedTicker.ticker, updatedTicker)
    processedCount += 1
  }

  return processedCount
}

function buildCategoryPath(category: Category, categoriesById: Map<number, Category>): string {
  const parent = category.parent_id == null ? null : categoriesById.get(category.parent_id) ?? null
  return parent ? `${buildCategoryPath(parent, categoriesById)}/${category.slug}` : category.slug
}

function getPathDepth(path: string) {
  return path.split('/').length
}
